import { FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query'

import { fetchEnrichedFormConfigByCode } from '@redux/api/helpers/fetchEnrichedFormConfigByCode'

import { transformResponseWithPagination } from '@helpers'
import { URLS } from '@constants'
import {
  FormShortDTO,
  GETDropDownFilterParams,
  GETForm,
  GETFormData,
  GETObjectDataParams,
  GETOptionGroupType,
  GETPreFillObjectDataRecordByFormID,
  ObjectDataRecord,
  POSTCopyForm,
  POSTForm,
  POSTOptionGroupType,
  PUTCellField,
} from '@types'

import { commonAPI } from './common.api'
import { BaseQueryMeta, BaseQueryResult } from '@reduxjs/toolkit/dist/query/baseQueryTypes'

export const formApi = commonAPI.injectEndpoints({
  endpoints: build => ({
    fetchAllForm: build.query<GETFormData, GETObjectDataParams | void>({
      query: params => ({
        url: URLS.FRONT_CFG + '/form',
        params: {
          page: params?.page,
          size: params?.size,
          sort: params?.sort,
          objectCode: params?.objectCode,
          searchPattern: params?.searchPattern,
        },
      }),
      transformResponse: (output: FormShortDTO[], meta: FetchBaseQueryMeta) =>
        transformResponseWithPagination<FormShortDTO[]>(output, meta),
      providesTags: ['AllForms', 'AllConfigs'],
    }),
    fetchAllFormUncertain: build.query<GETFormData, GETObjectDataParams | void>({
      query: params => ({
        url: URLS.FRONT_CFG + '/form/uncertain',
        params: {
          page: params?.page,
          size: params?.size,
          sort: params?.sort,
        },
      }),
      transformResponse: (output: FormShortDTO[], meta: FetchBaseQueryMeta) =>
        transformResponseWithPagination<FormShortDTO[]>(output, meta),
      providesTags: ['UncertainForms'],
    }),
    fetchAllFormByObjectCode: build.query<FormShortDTO[], string>({
      query: objectCode => ({
        url: URLS.FRONT_CFG + `/form/object/code/${objectCode}`,
      }),
      providesTags: ['AllForms', 'AllConfigs'],
    }),
    fetchForm: build.query<GETForm, { code: string | void | null; cacheId?: string }>({
      query: ({ code, cacheId }) => ({
        url: URLS.FRONT_CFG + `/form/code/${code}`,
      }),
      providesTags: ['SingleForm', 'AllConfigs'],
    }),
    fetchEnrichedFormConfigByCode: build.query<GETForm, { code: string | null; cacheId?: string }>({
      queryFn: fetchEnrichedFormConfigByCode,
      providesTags: ['SingleForm', 'AllConfigs'],
    }),
    createForm: build.mutation<GETForm, POSTForm>({
      query: form => ({
        url: URLS.FRONT_CFG + '/form',
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['UndefinedViews', 'SingleForm', 'AllForms', 'UncertainForms'],
    }),
    updateForm: build.mutation<GETForm, POSTForm>({
      query: form => ({
        url: URLS.FRONT_CFG + '/form',
        method: 'POST',
        body: form,
      }),
      invalidatesTags: [
        'UndefinedViews',
        'SingleForm',
        'UncertainForms',
        'AllForms',
        'FormContainers',
      ],
    }),
    deleteForm: build.mutation<GETForm, number | string>({
      query: id => ({
        url: URLS.FRONT_CFG + `/form/id/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UncertainForms', 'GroupForms', 'AllForms'],
    }),
    fetchFormGroups: build.query<GETOptionGroupType[], void>({
      query: () => ({
        url: URLS.FRONT_CFG + 'group/form',
      }),
      providesTags: ['GroupForms'],
    }),
    deleteFormByCode: build.mutation<GETForm, string>({
      query: code => ({
        url: URLS.FRONT_CFG + `/form/code/${code}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UncertainForms', 'GroupForms', 'AllForms'],
    }),
    updateFormGroups: build.mutation<GETOptionGroupType[], POSTOptionGroupType[]>({
      query: newGroups => ({
        url: URLS.FRONT_CFG + '/group/form',
        method: 'POST',
        body: newGroups,
      }),
      invalidatesTags: ['UncertainForms'],
    }),
    copyForm: build.mutation<GETForm, POSTCopyForm>({
      query: formData => ({
        url: URLS.FRONT_CFG + '/form/copy',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['UncertainForms', 'AllForms'],
    }),
    // TODO Использовать fetchObjectDataByFormCode вместо fetchObjectDataRecordEnrichedById
    // Статус: fetchObjectDataByFormCode переведён на executeCommand (useFetchObjectDataWithFixedKey)
    fetchPreFillObjectDataByFormCode: build.mutation<
      ObjectDataRecord,
      GETPreFillObjectDataRecordByFormID
    >({
      query: ({ formCode, objectCode, objectId }) => ({
        url: URLS.FRONT_API + `/data/form/code/${formCode}/preFill`,
        params: {
          objectCode,
          objectId,
        },
      }),
    }),
    updateViewCellField: build.mutation<ObjectDataRecord, PUTCellField>({
      query: ({ viewId, record, contextId }) => ({
        url: URLS.FRONT_API + `/data/view/${viewId}/update`,
        method: 'PUT',
        body: record,
        params: {
          contextId,
        },
      }),
      invalidatesTags: ['ObjectData', 'AuditData'],
    }),
    createViewCellField: build.mutation<ObjectDataRecord, PUTCellField>({
      query: ({ viewId, record, contextId }) => ({
        url: URLS.FRONT_API + `/data/view/${viewId}/update`,
        method: 'POST',
        body: record,
        params: {
          contextId,
        },
      }),
      invalidatesTags: ['ObjectData', 'AuditData'],
    }),
    fetchDropDownFilter: build.query<Record<string, string[]>, GETDropDownFilterParams>({
      query: ({ contextId, elementId, fieldId, valueId, columnId, dropDownEntityType }) => ({
        url: URLS.FRONT_CFG + `/coreApi/form/dropDownFilter/${contextId}`,
        params: { elementId, fieldId, valueId, columnId, dropDownEntityType },
      }),
    }),
  }),
})

export const {
  useFetchAllFormQuery,
  useFetchAllFormByObjectCodeQuery,
  useFetchFormQuery,
  useFetchEnrichedFormConfigByCodeQuery,
  useDeleteFormMutation,
  useCreateFormMutation,
  useUpdateFormMutation,
  useFetchAllFormUncertainQuery,
  useDeleteFormByCodeMutation,
  useFetchFormGroupsQuery,
  useUpdateFormGroupsMutation,
  useCopyFormMutation,
  useUpdateViewCellFieldMutation,
  useCreateViewCellFieldMutation,
  useFetchPreFillObjectDataByFormCodeMutation,
  useFetchDropDownFilterQuery,
} = formApi
